import { StoreError, MenuAPIErrorType, MenuAPIErrorCode } from '../types/menuApi';

/**
 * Error handling utilities for the new Menu API
 */

export interface ErrorDisplayConfig {
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
}

/**
 * Handle failures from menu API responses
 */
export function handleMenuFailures(failures: StoreError[], displayConfig: ErrorDisplayConfig): void {
  failures.forEach(error => {
    switch (error.errorType) {
      case MenuAPIErrorType.STORE_NOT_FOUND:
        displayConfig.showWarning(
          `Store ${error.storeNumber} not found - may need to update store list`
        );
        break;
        
      case MenuAPIErrorType.MENU_NOT_AVAILABLE:
        displayConfig.showInfo(
          `Store ${error.storeNumber} has no menu available`
        );
        break;
        
      case MenuAPIErrorType.MENU_FETCH_FAILED:
        displayConfig.showError(
          `Failed to load menu for store ${error.storeNumber}: ${error.message}`
        );
        break;
        
      case MenuAPIErrorType.MENU_PARSE_ERROR:
        displayConfig.showError(
          `Invalid menu format for store ${error.storeNumber}: ${error.message}`
        );
        break;
        
      case MenuAPIErrorType.CACHE_ERROR:
        displayConfig.showError(
          `Cache system issue for store ${error.storeNumber}. This may resolve automatically.`
        );
        break;
        
      case MenuAPIErrorType.SYSTEM_ERROR:
        displayConfig.showError(
          `System error for store ${error.storeNumber}: ${error.message}. Please contact support.`
        );
        break;
        
      default:
        displayConfig.showError(
          `Store ${error.storeNumber}: ${error.message}`
        );
    }
  });
}

/**
 * Get error severity level for UI styling
 */
export function getErrorSeverity(errorType: string): 'error' | 'warning' | 'info' {
  switch (errorType) {
    case MenuAPIErrorType.STORE_NOT_FOUND:
      return 'warning';
    case MenuAPIErrorType.MENU_NOT_AVAILABLE:
      return 'info';
    case MenuAPIErrorType.MENU_FETCH_FAILED:
    case MenuAPIErrorType.MENU_PARSE_ERROR:
    case MenuAPIErrorType.SYSTEM_ERROR:
      return 'error';
    case MenuAPIErrorType.CACHE_ERROR:
      return 'warning';
    default:
      return 'error';
  }
}

/**
 * Check if error is retryable
 */
export function isRetryableError(errorCode: string): boolean {
  const retryableCodes = [
    MenuAPIErrorCode.MENU_FETCH_500,
    MenuAPIErrorCode.CACHE_500,
    MenuAPIErrorCode.SYSTEM_500
  ];
  return retryableCodes.includes(errorCode as MenuAPIErrorCode);
}

/**
 * Group errors by type for summary display
 */
export function groupErrorsByType(errors: StoreError[]): Record<string, StoreError[]> {
  return errors.reduce((groups, error) => {
    const type = error.errorType;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(error);
    return groups;
  }, {} as Record<string, StoreError[]>);
}

/**
 * Generate user-friendly error summary
 */
export function generateErrorSummary(errors: StoreError[]): string {
  if (errors.length === 0) return '';
  
  const grouped = groupErrorsByType(errors);
  const summaryParts: string[] = [];
  
  Object.entries(grouped).forEach(([type, typeErrors]) => {
    const count = typeErrors.length;
    const stores = typeErrors.map(e => e.storeNumber).join(', ');
    
    switch (type) {
      case MenuAPIErrorType.STORE_NOT_FOUND:
        summaryParts.push(`${count} store(s) not found: ${stores}`);
        break;
      case MenuAPIErrorType.MENU_NOT_AVAILABLE:
        summaryParts.push(`${count} store(s) with no menu: ${stores}`);
        break;
      case MenuAPIErrorType.MENU_FETCH_FAILED:
        summaryParts.push(`${count} store(s) failed to fetch: ${stores}`);
        break;
      default:
        summaryParts.push(`${count} store(s) with ${type}: ${stores}`);
    }
  });
  
  return summaryParts.join('; ');
}

/**
 * Check if failures contain only non-critical errors
 */
export function hasOnlyNonCriticalErrors(errors: StoreError[]): boolean {
  const nonCriticalTypes = [
    MenuAPIErrorType.STORE_NOT_FOUND,
    MenuAPIErrorType.MENU_NOT_AVAILABLE
  ];
  
  return errors.every(error => nonCriticalTypes.includes(error.errorType as MenuAPIErrorType));
}

/**
 * Get recommended action for error type
 */
export function getRecommendedAction(errorType: string): string {
  switch (errorType) {
    case MenuAPIErrorType.STORE_NOT_FOUND:
      return 'Update store list or verify store numbers';
    case MenuAPIErrorType.MENU_NOT_AVAILABLE:
      return 'Check if menu exists for this store';
    case MenuAPIErrorType.MENU_FETCH_FAILED:
      return 'Retry the request or check store status';
    case MenuAPIErrorType.MENU_PARSE_ERROR:
      return 'Contact support - data format issue';
    case MenuAPIErrorType.CACHE_ERROR:
      return 'Wait and retry - may resolve automatically';
    case MenuAPIErrorType.SYSTEM_ERROR:
      return 'Contact support if problem persists';
    default:
      return 'Contact support for assistance';
  }
}